﻿/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

class HeaderLocationSelector extends HTMLElement {
  connectedCallback() {
    this._nameEl = this.querySelector('[data-location-name]');
    this._stores = [];

    try {
      this._stores = JSON.parse(this.dataset.stores || '[]');
    } catch (error) {
      this._stores = [];
    }

    this._defaultLabel = this.dataset.defaultLabel || 'Select a store';

    // The click is handled by the delegated [data-action="open-store-picker"] listener
    // near the bottom of this file, which covers every surface offering the picker.
    this.updateLabel();
    document.addEventListener('age-gate:store-selected', () => this.updateLabel());
    window.addEventListener('storage', (event) => {
      if (event.key === '_selected_location_id') {
        this.updateLabel();
      }
    });
  }

  _readLocationId() {
    const raw = window.localStorage.getItem('_selected_location_id');
    if (!raw) return null;

    try {
      const parsed = JSON.parse(raw);
      return parsed == null ? null : String(parsed);
    } catch (error) {
      return raw;
    }
  }

  updateLabel() {
    if (!this._nameEl) return;

    const locationId = this._readLocationId();
    const store = this._stores.find((entry) => String(entry.id) === String(locationId));
    this._nameEl.textContent = store?.name || this._defaultLabel;
  }
}

if (!window.customElements.get('header-location-selector')) {
  window.customElements.define('header-location-selector', HeaderLocationSelector);
}

class StoreInfoSection extends HTMLElement {
  connectedCallback() {
    this._headingEl = this.querySelector('[data-store-heading]');
    this._addressEl = this.querySelector('[data-store-address]');
    this._hoursEl = this.querySelector('[data-store-hours]');
    this._detailsEl = this.querySelector('.store-info__details');
    this._contactEl = this.querySelector('[data-store-contact]');
    this._phoneEl = this.querySelector('[data-store-phone]');
    this._phoneLinkEl = this.querySelector('[data-store-phone-link]');
    this._imageEl = this.querySelector('[data-store-image], .store-info__image');
    this._mediaEl = this.querySelector('[data-store-media]');
    this._innerEl = this.querySelector('[data-store-inner]');
    this._stores = [];

    const storesSource = this.dataset.storesSource;
    if (storesSource) {
      const sourceEl = document.getElementById(storesSource);
      if (sourceEl) {
        try {
          this._stores = JSON.parse(sourceEl.textContent || '[]');
        } catch (error) {
          this._stores = [];
        }
      }
    }

    this._headingPrefix = this.dataset.headingPrefix || 'Shopping at';
    this._fallback = {
      heading: this.dataset.fallbackHeading || '',
      address: this.dataset.fallbackAddress || '',
      hours: this.dataset.fallbackHours || '',
      phone: this.dataset.fallbackPhone || '',
      image: this.dataset.fallbackImage || '',
    };

    // Same as above: the delegated listener near the bottom of this file handles it.
    this.updateContent();
    document.addEventListener('age-gate:store-selected', () => this.updateContent());
    window.addEventListener('storage', (event) => {
      if (event.key === '_selected_location_id') {
        this.updateContent();
      }
    });
  }

  _readLocationId() {
    const raw = window.localStorage.getItem('_selected_location_id');
    if (!raw) return null;

    try {
      const parsed = JSON.parse(raw);
      return parsed == null ? null : String(parsed);
    } catch (error) {
      return raw;
    }
  }

  _formatAddress(store) {
    const line1 = [store.address, store.address2].filter(Boolean).join(', ');
    const line2 = [store.city, store.provinceCode || store.province, store.zip]
      .filter(Boolean)
      .join(', ');
    return [line1, line2].filter(Boolean).join(', ');
  }

  _formatHours(store) {
    if (!store) return '';

    // A range first, because this sits under a "Store Hours" label. storeUntil is a
    // closing time — "Until 11:00 PM" — which answers a different question and reads
    // wrong as the whole of today's hours. It stays as the fallback for a store that
    // only records a closing time.
    const open = String(store.storeOpenTime || '').trim();
    const close = String(store.storeCloseTime || '').trim();
    if (open && close) return `${open} - ${close}`;

    if (store.storeUntil) {
      const until = String(store.storeUntil).trim();
      if (until && !/^until$/i.test(until)) return until;
    }

    if (store.storeHours) {
      const hours = String(store.storeHours).trim();
      if (hours && hours !== '[object Object]') return hours;
    }

    return '';
  }

  _setText(el, value, { allowBreaks = false } = {}) {
    if (!el) return;

    const text = value || '';
    if (allowBreaks) {
      el.replaceChildren();
      text.split('\n').forEach((line, index) => {
        if (index > 0) el.appendChild(document.createElement('br'));
        el.appendChild(document.createTextNode(line));
      });
      return;
    }

    el.textContent = text;
  }

  updateContent() {
    const locationId = this._readLocationId();
    const store = this._stores.find((entry) => String(entry.id) === String(locationId));

    const heading = store
      ? [this._headingPrefix, store.name].filter(Boolean).join(' ').trim()
      : this._fallback.heading;
    const address = store ? this._formatAddress(store) : this._fallback.address;
    const hours = store ? this._formatHours(store) || this._fallback.hours : this._fallback.hours;
    // Selected store: only show its own phone (no fallback). No store: use section fallback.
    const phone = store
      ? String(store.phone || '').trim()
      : String(this._fallback.phone || '').trim();
    const image = store ? store.image || this._fallback.image : this._fallback.image;

    this._setText(this._headingEl, heading);
    this._setText(this._addressEl, address, { allowBreaks: true });
    this._setText(this._hoursEl, hours);
    this._setText(this._phoneEl, phone);

    if (this._contactEl) {
      this._contactEl.hidden = !phone;
    }

    if (this._detailsEl) {
      const hoursVisible = Boolean(this._hoursEl && String(hours || '').trim());
      const contactVisible = Boolean(phone);
      this._detailsEl.hidden = !hoursVisible && !contactVisible;
    }

    if (this._phoneLinkEl) {
      this._phoneLinkEl.href = phone
        ? `tel:${String(phone).replace(/[\s()-]/g, '')}`
        : '#';
    }

    this._applyImage(image, heading);
  }

  // A store with no image of its own and no uploaded fallback shows no image at all,
  // rather than a stock placeholder standing in for a real storefront photo.
  _applyImage(image, heading) {
    const hasImage = Boolean(image);

    if (this._imageEl) {
      if (hasImage) {
        this._imageEl.setAttribute('src', image);
        this._imageEl.setAttribute('alt', heading || '');
      } else {
        this._imageEl.removeAttribute('src');
        this._imageEl.setAttribute('alt', '');
      }
      if (this._imageEl.hasAttribute('srcset')) {
        this._imageEl.removeAttribute('srcset');
      }
      this._imageEl.hidden = !hasImage;
    }

    if (this._mediaEl) this._mediaEl.hidden = !hasImage;
    if (this._innerEl) {
      this._innerEl.classList.toggle('store-info__inner--no-image', !hasImage);
    }
  }
}

if (!window.customElements.get('store-info-section')) {
  window.customElements.define('store-info-section', StoreInfoSection);
}

class MegaMenuTabs extends HTMLElement {
  connectedCallback() {
    this._tabs = Array.from(this.querySelectorAll('[data-tab-trigger]'));
    this._panels = Array.from(this.querySelectorAll('[data-tab-panel]'));

    if (!this._tabs.length || !this._panels.length) {
      return;
    }

    this._onTabClick = (event) => {
      const trigger = event.target.closest('[data-tab-trigger]');
      if (!trigger || !this.contains(trigger)) {
        return;
      }

      event.preventDefault();
      this.activateTab(trigger.dataset.tabTrigger);
    };

    this._onKeyDown = (event) => {
      const currentIndex = this._tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
      if (currentIndex === -1) {
        return;
      }

      let nextIndex = currentIndex;

      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
        nextIndex = (currentIndex + 1) % this._tabs.length;
      } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
        nextIndex = (currentIndex - 1 + this._tabs.length) % this._tabs.length;
      } else if (event.key === 'Home') {
        nextIndex = 0;
      } else if (event.key === 'End') {
        nextIndex = this._tabs.length - 1;
      } else {
        return;
      }

      event.preventDefault();
      this.activateTab(this._tabs[nextIndex].dataset.tabTrigger, true);
    };

    this._onDropdownOpen = () => {
      if (!this.closest('.mega-menu:not([hidden])')) {
        return;
      }

      const firstTab = this._tabs[0];
      if (firstTab) {
        this.activateTab(firstTab.dataset.tabTrigger);
      }
    };

    this._onBlockSelect = (event) => {
      const trigger = event.target.closest('[data-tab-trigger]');
      const panel = event.target.closest('[data-tab-panel]');

      if (trigger) {
        this.activateTab(trigger.dataset.tabTrigger, true);
      } else if (panel) {
        this.activateTab(panel.dataset.tabPanel, true);
      }
    };

    this.addEventListener('click', this._onTabClick);
    this.addEventListener('keydown', this._onKeyDown);
    this.addEventListener('shopify:block:select', this._onBlockSelect);

    const desktopNavigation = this.closest('desktop-navigation');
    if (desktopNavigation) {
      desktopNavigation.addEventListener('desktop-nav:dropdown:open', this._onDropdownOpen);
      this._desktopNavigation = desktopNavigation;
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._onTabClick);
    this.removeEventListener('keydown', this._onKeyDown);
    this.removeEventListener('shopify:block:select', this._onBlockSelect);

    if (this._desktopNavigation) {
      this._desktopNavigation.removeEventListener('desktop-nav:dropdown:open', this._onDropdownOpen);
    }
  }

  activateTab(tabId, focusTab = false) {
    if (!tabId) {
      return;
    }

    this._tabs.forEach((tab) => {
      const isActive = tab.dataset.tabTrigger === tabId;
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      tab.tabIndex = isActive ? 0 : -1;

      if (isActive && focusTab) {
        tab.focus();
      }
    });

    this._panels.forEach((panel) => {
      const isActive = panel.dataset.tabPanel === tabId;
      panel.toggleAttribute('hidden', !isActive);
    });
  }
}

if (!window.customElements.get('mega-menu-tabs')) {
  window.customElements.define('mega-menu-tabs', MegaMenuTabs);
}

function getThumbnailSlideshowAutoplayMs(slideshow) {
  const duration = getComputedStyle(slideshow).getPropertyValue('--section-autoplay-duration').trim();

  if (!duration || duration === '0s') {
    return 5000;
  }

  return parseFloat(duration) * 1000;
}

function lockThumbnailSlideshowHeight(slideshow, { remeasure = false } = {}) {
  const firstSlide = slideshow.querySelector('slide-show-item');

  if (!firstSlide) {
    return;
  }

  const width = slideshow.getBoundingClientRect().width;

  if (width < 1) {
    return;
  }

  let height = 0;

  if (remeasure || !slideshow.dataset.thumbnailHeightRatio) {
    if (!firstSlide.hasAttribute('hidden')) {
      slideshow.classList.remove('thumbnail-slideshow--locked-height');
      slideshow.style.removeProperty('--thumbnail-slideshow-locked-height');
      height = firstSlide.getBoundingClientRect().height;

      if (height > 0) {
        slideshow.dataset.thumbnailHeightRatio = String(height / width);
      }
    } else if (slideshow.dataset.thumbnailHeightRatio) {
      height = width * parseFloat(slideshow.dataset.thumbnailHeightRatio);
    }
  } else {
    height = width * parseFloat(slideshow.dataset.thumbnailHeightRatio);
  }

  if (!(height > 0)) {
    return;
  }

  const maxHeight = parseFloat(
    getComputedStyle(slideshow).getPropertyValue('--thumbnail-slideshow-max-height')
  );

  if (maxHeight > 0) {
    height = Math.min(height, maxHeight);
  }

  const nextHeight = `${Math.round(height)}px`;

  if (slideshow.style.getPropertyValue('--thumbnail-slideshow-locked-height') === nextHeight) {
    slideshow.classList.add('thumbnail-slideshow--locked-height');
    return;
  }

  slideshow.style.setProperty('--thumbnail-slideshow-locked-height', nextHeight);
  slideshow.classList.add('thumbnail-slideshow--locked-height');
}

function setupThumbnailSlideshowHeightLock(slideshow) {
  if (slideshow.dataset.thumbnailHeightLockInit) {
    return;
  }

  slideshow.dataset.thumbnailHeightLockInit = 'true';

  const sync = (options) => lockThumbnailSlideshowHeight(slideshow, options);
  const images = slideshow.querySelectorAll('slide-show-item:first-child img');

  sync({ remeasure: true });

  images.forEach((image) => {
    if (image.complete) {
      return;
    }

    image.addEventListener('load', () => sync({ remeasure: true }), { once: true });
  });

  if (typeof ResizeObserver === 'function') {
    const resizeObserver = new ResizeObserver(() => sync());
    resizeObserver.observe(slideshow);
  } else {
    window.addEventListener('resize', () => sync());
  }
}

function setupThumbnailSlideshow(slideshow) {
  if (!slideshow?.classList?.contains('thumbnail-slideshow') || slideshow.dataset.thumbnailSlideshowInit) {
    return;
  }

  slideshow.dataset.thumbnailSlideshowInit = 'true';
  setupThumbnailSlideshowHeightLock(slideshow);

  slideshow.querySelector('[data-action="prev"]')?.addEventListener('click', (event) => {
    event.preventDefault();
    slideshow.pausePlayer();
    slideshow.previous();
    slideshow.startPlayer();
  });

  slideshow.querySelector('[data-action="next"]')?.addEventListener('click', (event) => {
    event.preventDefault();
    slideshow.pausePlayer();
    slideshow.next();
    slideshow.startPlayer();
  });

  slideshow.startPlayer = function thumbnailStartPlayer() {
    if (!this.hasAttribute('auto-play')) {
      return;
    }

    this.style.setProperty('--section-animation-play-state', 'running');
    clearInterval(this._thumbnailAutoplayTimer);
    this._thumbnailAutoplayTimer = setInterval(() => {
      if (this.style.getPropertyValue('--section-animation-play-state') !== 'running') {
        return;
      }

      this.next();
    }, getThumbnailSlideshowAutoplayMs(this));
  };

  slideshow.pausePlayer = function thumbnailPausePlayer() {
    this.style.setProperty('--section-animation-play-state', 'paused');
    clearInterval(this._thumbnailAutoplayTimer);
  };

  if (slideshow.hasAttribute('auto-play')) {
    slideshow.startPlayer();
  }
}

function initThumbnailSlideshows(root = document) {
  const boot = () => {
    root.querySelectorAll('slide-show.thumbnail-slideshow').forEach(setupThumbnailSlideshow);
  };

  if (window.customElements.get('slide-show')) {
    boot();
    return;
  }

  window.customElements.whenDefined('slide-show').then(boot);
}

document.addEventListener('DOMContentLoaded', () => initThumbnailSlideshows());
document.addEventListener('shopify:section:load', (event) => {
  initThumbnailSlideshows(event.target);
});

initThumbnailSlideshows();

function getHeroSliderAutoplayMs(slideshow) {
  const duration = getComputedStyle(slideshow).getPropertyValue('--section-autoplay-duration').trim();

  if (!duration || duration === '0s') {
    return 6000;
  }

  return parseFloat(duration) * 1000;
}

function setupHeroSlider(slideshow) {
  if (!slideshow?.classList?.contains('hero-slider') || slideshow.dataset.heroSliderInit) {
    return;
  }

  slideshow.dataset.heroSliderInit = 'true';

  slideshow.querySelector('[data-action="prev"]')?.addEventListener('click', (event) => {
    event.preventDefault();
    slideshow.pausePlayer();
    slideshow.previous();
    slideshow.startPlayer();
  });

  slideshow.querySelector('[data-action="next"]')?.addEventListener('click', (event) => {
    event.preventDefault();
    slideshow.pausePlayer();
    slideshow.next();
    slideshow.startPlayer();
  });

  slideshow.startPlayer = function heroSliderStartPlayer() {
    if (!this.hasAttribute('auto-play')) {
      return;
    }

    this.style.setProperty('--section-animation-play-state', 'running');
    clearInterval(this._heroSliderAutoplayTimer);
    this._heroSliderAutoplayTimer = setInterval(() => {
      if (this.style.getPropertyValue('--section-animation-play-state') !== 'running') {
        return;
      }

      this.next();
    }, getHeroSliderAutoplayMs(this));
  };

  slideshow.pausePlayer = function heroSliderPausePlayer() {
    this.style.setProperty('--section-animation-play-state', 'paused');
    clearInterval(this._heroSliderAutoplayTimer);
  };

  if (slideshow.hasAttribute('auto-play')) {
    slideshow.startPlayer();
  }
}

function initHeroSliders(root = document) {
  const boot = () => {
    root.querySelectorAll('slide-show.hero-slider').forEach(setupHeroSlider);
  };

  if (window.customElements.get('slide-show')) {
    boot();
    return;
  }

  window.customElements.whenDefined('slide-show').then(boot);
}

document.addEventListener('DOMContentLoaded', () => initHeroSliders());
document.addEventListener('shopify:section:load', (event) => {
  initHeroSliders(event.target);
});

initHeroSliders();

/* ------------------------------------------------------------------ */
/* Flyers section slider                                              */
/* ------------------------------------------------------------------ */

function setupFlyersSection(slideshow) {
  if (!slideshow?.classList?.contains('flyers-section__slider') || slideshow.dataset.flyersSectionInit) {
    return;
  }

  slideshow.dataset.flyersSectionInit = 'true';

  slideshow.querySelector('[data-action="prev"]')?.addEventListener('click', (event) => {
    event.preventDefault();
    slideshow.pausePlayer?.();
    slideshow.previous();
    slideshow.startPlayer?.();
  });

  slideshow.querySelector('[data-action="next"]')?.addEventListener('click', (event) => {
    event.preventDefault();
    slideshow.pausePlayer?.();
    slideshow.next();
    slideshow.startPlayer?.();
  });

  slideshow.startPlayer = function flyersSectionStartPlayer() {
    if (!this.hasAttribute('auto-play')) {
      return;
    }

    this.style.setProperty('--section-animation-play-state', 'running');
    clearInterval(this._flyersSectionAutoplayTimer);
    this._flyersSectionAutoplayTimer = setInterval(() => {
      if (this.style.getPropertyValue('--section-animation-play-state') !== 'running') {
        return;
      }

      this.next();
    }, getHeroSliderAutoplayMs(this));
  };

  slideshow.pausePlayer = function flyersSectionPausePlayer() {
    this.style.setProperty('--section-animation-play-state', 'paused');
    clearInterval(this._flyersSectionAutoplayTimer);
  };

  if (slideshow.hasAttribute('auto-play')) {
    slideshow.startPlayer();
  }
}

function initFlyersSections(root = document) {
  const boot = () => {
    root.querySelectorAll('slide-show.flyers-section__slider').forEach(setupFlyersSection);
  };

  if (window.customElements.get('slide-show')) {
    boot();
    return;
  }

  window.customElements.whenDefined('slide-show').then(boot);
}

document.addEventListener('DOMContentLoaded', () => initFlyersSections());
document.addEventListener('shopify:section:load', (event) => {
  initFlyersSections(event.target);
});

initFlyersSections();

/* ------------------------------------------------------------------ */
/* Featured deal - mobile dots / slide sync                           */
/* ------------------------------------------------------------------ */

function setupFeaturedDeal(carousel) {
  if (!carousel || carousel.dataset.featuredDealInit) {
    return;
  }

  const track = carousel.querySelector('.featured-deal__track');
  const pageDots = carousel.querySelector('.featured-deal__dots');
  const cards = Array.from(carousel.querySelectorAll('.featured-deal__card'));
  const mobileNav = carousel.querySelector('prev-next-buttons.featured-deal__mobile-arrows');
  const desktopNav = carousel.querySelector('prev-next-buttons.featured-deal__nav');

  if (!track || cards.length < 2) {
    return;
  }

  carousel.dataset.featuredDealInit = 'true';

  let isProgrammaticScroll = false;

  const getSelectedIndex = () => {
    const direction = window.themeVariables?.settings?.direction === 'rtl' ? -1 : 1;
    const scrollLeft = track.scrollLeft * direction;
    const cardWidth = cards[0].offsetWidth + (parseInt(getComputedStyle(carousel).getPropertyValue('--multi-column-column-gap'), 10) || 0);
    if (cardWidth <= 0) {
      return 0;
    }
    return Math.max(0, Math.min(cards.length - 1, Math.round(scrollLeft / cardWidth)));
  };

  const syncNav = (index) => {
    if (pageDots) {
      pageDots.selectedIndex = index;
    }

    if (mobileNav) {
      mobileNav.isPrevDisabled = index <= 0;
      mobileNav.isNextDisabled = index >= cards.length - 1;
    }

    if (desktopNav) {
      desktopNav.isPrevDisabled = index <= 0;
      desktopNav.isNextDisabled = index >= cards.length - 1;
    }
  };

  const select = (index, animate = true) => {
    const clampIndex = Math.max(0, Math.min(cards.length - 1, index));
    const direction = window.themeVariables?.settings?.direction === 'rtl' ? -1 : 1;
    const columnGap = parseInt(getComputedStyle(carousel).getPropertyValue('--multi-column-column-gap'), 10) || 0;
    const left = direction * (cards[0].offsetWidth + columnGap) * clampIndex;

    isProgrammaticScroll = true;
    track.scrollTo({ left, behavior: animate ? 'smooth' : 'auto' });
    syncNav(clampIndex);

    window.setTimeout(() => {
      isProgrammaticScroll = false;
    }, 400);
  };

  if (pageDots) {
    carousel.addEventListener('page-dots:changed', (event) => {
      select(event.detail.index, true);
    });
  }

  track.addEventListener('scroll', () => {
    if (isProgrammaticScroll) {
      return;
    }
    syncNav(getSelectedIndex());
  }, { passive: true });

  syncNav(0);
}

function initFeaturedDeals(root = document) {
  root.querySelectorAll('multi-column.featured-deal__carousel').forEach(setupFeaturedDeal);
}

document.addEventListener('DOMContentLoaded', () => initFeaturedDeals());
document.addEventListener('shopify:section:load', (event) => {
  initFeaturedDeals(event.target);
});

initFeaturedDeals();

/* ------------------------------------------------------------------ */
/* Featured collections (shop deals) - arrow visibility sync         */
/* ------------------------------------------------------------------ */

function setupFeaturedCollectionsArrows(productList) {
  if (!productList || productList.dataset.featuredCollectionsArrowsInit) {
    return;
  }

  const prevNext = productList.querySelector('.product-list__prev-next');
  const inner = productList.querySelector('.product-list__inner');
  const prevBtn = prevNext?.querySelector('.prev-next-button--prev');
  const nextBtn = prevNext?.querySelector('.prev-next-button--next');

  if (!prevNext || !inner || !prevBtn || !nextBtn) {
    return;
  }

  productList.dataset.featuredCollectionsArrowsInit = 'true';

  const syncArrows = () => {
    const direction = window.themeVariables?.settings?.direction === 'rtl' ? -1 : 1;
    const scrollLeft = inner.scrollLeft * direction;
    const maxScroll = inner.scrollWidth - inner.clientWidth;

    if (maxScroll <= 0) {
      prevBtn.disabled = true;
      nextBtn.disabled = true;
      return;
    }

    prevBtn.disabled = scrollLeft <= 1;
    nextBtn.disabled = scrollLeft >= maxScroll - 1;
  };

  inner.addEventListener('scroll', syncArrows, { passive: true });
  window.addEventListener('resize', syncArrows);
  productList.addEventListener('prev-next:prev', syncArrows);
  productList.addEventListener('prev-next:next', syncArrows);
  syncArrows();
}

function initFeaturedCollectionsArrows(root = document) {
  root.querySelectorAll('.shopify-section--featured-collections product-list').forEach(setupFeaturedCollectionsArrows);
}

document.addEventListener('DOMContentLoaded', () => initFeaturedCollectionsArrows());
document.addEventListener('shopify:section:load', (event) => {
  initFeaturedCollectionsArrows(event.target);
});

initFeaturedCollectionsArrows();

/* ------------------------------------------------------------------ */
/* Catalog facets - preserve province/location on Clear all           */
/* ------------------------------------------------------------------ */

function getPreservedFacetInputs(root = document) {
  return Array.from(
    root.querySelectorAll('#facet-filters-form input[type="hidden"][data-preserve-on-clear]')
  );
}

function withPreservedFacetParams(urlString) {
  const url = new URL(urlString, window.location.origin);
  const currentUrl = new URL(window.location.href);

  getPreservedFacetInputs().forEach((input) => {
    if (!input.name) {
      return;
    }

    const value = currentUrl.searchParams.get(input.name) || input.value;
    if (value) {
      url.searchParams.set(input.name, value);
    }
  });

  return url.toString();
}

function patchFacetClearFilters() {
  const FacetFilters = customElements.get('facet-filters')?.constructor;
  if (!FacetFilters || FacetFilters.prototype.__vbClearPatched) {
    return;
  }

  FacetFilters.prototype.__vbClearPatched = true;

  const originalClear = FacetFilters.prototype._onFiltersCleared;

  FacetFilters.prototype._onFiltersCleared = function onFiltersCleared(event, target) {
    const isClearAll =
      target.classList.contains('tag-link--catalog-clear') ||
      target.classList.contains('drawer__header-action--clear');

    if (isClearAll) {
      const baseHref = target.getAttribute('href');
      if (baseHref) {
        const nextUrl = withPreservedFacetParams(baseHref);
        target.setAttribute('href', nextUrl);
      }
    }

    return originalClear.call(this, event, target);
  };
}

if (customElements.get('facet-filters')) {
  patchFacetClearFilters();
} else {
  customElements.whenDefined('facet-filters').then(patchFacetClearFilters);
}

class LocationHeaderSection extends HTMLElement {
  connectedCallback() {
    this._headingEl = this.querySelector('[data-lh-heading]');
    this._addressEl = this.querySelector('[data-lh-address]');
    this._addressWrapEl = this.querySelector('[data-lh-address-wrap]');
    this._hoursEl = this.querySelector('[data-lh-hours]');
    this._hoursWrapEl = this.querySelector('[data-lh-hours-wrap]');
    this._detailsEl = this.querySelector('.location-header__details');
    this._contactEl = this.querySelector('[data-lh-contact]');
    this._phoneEl = this.querySelector('[data-lh-phone]');
    this._phoneLinkEl = this.querySelector('[data-lh-phone-link]');
    this._statusEl = this.querySelector('[data-lh-status]');
    this._untilEl = this.querySelector('[data-lh-until]');
    this._untilWrapEl = this.querySelector('[data-lh-until-wrap]');
    this._directionsEl = this.querySelector('[data-lh-directions]');
    this._imageEl = this.querySelector('[data-lh-image], .location-header__image');
    this._mediaEl = this.querySelector('[data-lh-media]');
    this._innerEl = this.querySelector('[data-lh-inner]');
    this._stores = [];

    const storesSource = this.dataset.storesSource;
    if (storesSource) {
      const sourceEl = document.getElementById(storesSource);
      if (sourceEl) {
        try {
          this._stores = JSON.parse(sourceEl.textContent || '[]');
        } catch (error) {
          this._stores = [];
        }
      }
    }

    this._labelOpen = this.dataset.labelOpen || 'Open';
    this._labelClosed = this.dataset.labelClosed || 'Closed';
    this._layoutStyle = this.dataset.layoutStyle || 'default';
    this._isStorePage = this.dataset.storePage === 'true';
    this._secondaryLink = this.dataset.secondaryLink || '';
    this._fallback = {
      heading: this.dataset.fallbackHeading || '',
      address: this.dataset.fallbackAddress || '',
      hours: this.dataset.fallbackHours || '',
      until: this.dataset.fallbackUntil || '',
      phone: this.dataset.fallbackPhone || '',
      image: this.dataset.fallbackImage || '',
    };

    this.updateContent();
    document.addEventListener('age-gate:store-selected', () => this.updateContent());
    window.addEventListener('storage', (event) => {
      if (event.key === '_selected_location_id') {
        this.updateContent();
      }
    });
  }

  _readCurrentStoreLocationId() {
    const raw = window.localStorage.getItem('_selected_location_id');
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw);
      return parsed == null ? null : String(parsed);
    } catch (error) {
      return String(raw);
    }
  }

  _readLocationId() {
    try {
      const fromUrl = new URLSearchParams(window.location.search).get('location_id');
      if (fromUrl != null && String(fromUrl).trim() !== '') {
        return String(fromUrl).trim();
      }
    } catch (error) {
      /* ignore invalid URL */
    }

    return this._readCurrentStoreLocationId();
  }

  _formatAddress(store) {
    const line1 = [store.address, store.address2].filter(Boolean).join(' ');
    const line2 = [store.city, store.province || store.provinceCode].filter(Boolean).join(', ');
    return [line1, line2].filter(Boolean).join('  ');
  }

  _formatUntil(store) {
    if (!store || !store.storeUntil) return '';
    const until = String(store.storeUntil).trim();
    return until && !/^until$/i.test(until) ? until : '';
  }

  _formatHours(store) {
    if (!store || !store.storeHours) return '';
    const hours = String(store.storeHours).trim();
    if (!hours || hours === '[object Object]' || /^until\b/i.test(hours)) return '';
    return hours;
  }

  _parseClockToMinutes(value) {
    if (value == null || value === '') return null;
    if (typeof value === 'object') {
      if (value.hour != null) {
        const hours = Number(value.hour);
        const minutes = Number(value.minute || 0);
        if (!Number.isNaN(hours)) return hours * 60 + minutes;
      }
      value = value.time || value.value || '';
    }
    const raw = String(value).trim();
    if (!raw || raw === '[object Object]') return null;
    const isoMatch = raw.match(/T(\d{1,2}):(\d{2})/);
    if (isoMatch) return Number(isoMatch[1]) * 60 + Number(isoMatch[2]);
    const ampm = raw.match(/(\d{1,2})(?::(\d{2}))?\s*(AM|PM)/i);
    if (ampm) {
      let hours = Number(ampm[1]) % 12;
      if (/pm/i.test(ampm[3])) hours += 12;
      return hours * 60 + Number(ampm[2] || 0);
    }
    const twentyFour = raw.match(/^(\d{1,2}):(\d{2})/);
    if (twentyFour) return Number(twentyFour[1]) * 60 + Number(twentyFour[2]);
    return null;
  }

  _isOpen(store) {
    if (!store) return true;
    if (typeof store.available === 'boolean' && store.available === false) return false;
    const openMinutes = this._parseClockToMinutes(store.storeOpenTime);
    const closeMinutes = this._parseClockToMinutes(store.storeCloseTime);
    if (openMinutes == null && closeMinutes == null) return true;
    const now = new Date();
    const nowMinutes = now.getHours() * 60 + now.getMinutes();
    if (openMinutes != null && closeMinutes != null) {
      if (closeMinutes > openMinutes) return nowMinutes >= openMinutes && nowMinutes < closeMinutes;
      return nowMinutes >= openMinutes || nowMinutes < closeMinutes;
    }
    if (closeMinutes != null) return nowMinutes < closeMinutes;
    if (openMinutes != null) return nowMinutes >= openMinutes;
    return true;
  }

  _directionsUrl(store, address) {
    if (this._secondaryLink) return this._secondaryLink;
    if (store && store.latitude != null && store.longitude != null) {
      return `https://www.google.com/maps/dir/?api=1&destination=${store.latitude},${store.longitude}`;
    }
    if (address) return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`;
    return '#';
  }

  _setText(el, value) {
    if (!el) return;
    el.textContent = value || '';
  }

  updateContent() {
    // A store page is about the store named in its URL, so the server has already rendered
    // that store from the metaobject. Everything below resolves a store from ?location_id or
    // localStorage instead — the VISITOR's store — and writes it over the top. On
    // /pages/locations/<handle> that replaced the page's store with a different one, or,
    // when the visitor had none selected, wrote the empty section fallback over a correct
    // heading and left the page looking blank.
    if (this._isStorePage) return;

    let locationId = this._readLocationId();
    let store = this._stores.find((entry) => String(entry.id) === String(locationId));

    if (!store) {
      const currentId = this._readCurrentStoreLocationId();
      if (currentId && String(currentId) !== String(locationId)) {
        locationId = currentId;
        store = this._stores.find((entry) => String(entry.id) === String(locationId));
      }
    }

    const heading = store ? store.name : this._fallback.heading;
    const address = store ? this._formatAddress(store) : this._fallback.address;
    const hours = store ? this._formatHours(store) || this._fallback.hours : this._fallback.hours;
    const until = store ? this._formatUntil(store) || this._fallback.until : this._fallback.until;
    const phone = store ? String(store.phone || '').trim() : String(this._fallback.phone || '').trim();
    const image = store ? store.image || this._fallback.image : this._fallback.image;
    const isOpen = store ? this._isOpen(store) : true;

    this._setText(this._headingEl, heading);
    this._setText(this._addressEl, address);
    this._setText(this._hoursEl, hours);
    this._setText(this._phoneEl, phone);
    this._setText(this._untilEl, until);

    if (this._statusEl) {
      this._statusEl.textContent = isOpen ? this._labelOpen : this._labelClosed;
      this._statusEl.classList.toggle('is-closed', !isOpen);
    }
    if (this._untilWrapEl) this._untilWrapEl.hidden = !String(until || '').trim();
    if (this._hoursWrapEl) this._hoursWrapEl.hidden = !String(hours || '').trim();
    if (this._contactEl) this._contactEl.hidden = !phone;
    if (this._addressWrapEl) this._addressWrapEl.hidden = !String(address || '').trim();
    if (this._detailsEl) {
      const isStoreSection = this._layoutStyle === 'store_section';
      if (isStoreSection) {
        this._detailsEl.hidden = !String(address || '').trim() && !phone;
      } else {
        this._detailsEl.hidden = !String(hours || '').trim() && !phone;
      }
    }
    if (this._phoneLinkEl) {
      this._phoneLinkEl.href = phone ? `tel:${String(phone).replace(/[\s()-]/g, '')}` : '#';
    }
    if (this._directionsEl) {
      this._directionsEl.href = this._directionsUrl(store, address);
      if (!this._secondaryLink) {
        this._directionsEl.setAttribute('target', '_blank');
        this._directionsEl.setAttribute('rel', 'noopener');
      }
    }
    this._applyImage(image, heading);
  }

  // See StoreInfoSection._applyImage — no location image and no uploaded fallback means
  // the media column disappears instead of showing a stock placeholder.
  _applyImage(image, heading) {
    const hasImage = Boolean(image);

    if (this._imageEl) {
      if (hasImage) {
        this._imageEl.setAttribute('src', image);
        this._imageEl.setAttribute('alt', heading || '');
      } else {
        this._imageEl.removeAttribute('src');
        this._imageEl.setAttribute('alt', '');
      }
      if (this._imageEl.hasAttribute('srcset')) this._imageEl.removeAttribute('srcset');
      this._imageEl.hidden = !hasImage;
    }

    if (this._mediaEl) this._mediaEl.hidden = !hasImage;
    if (this._innerEl) {
      this._innerEl.classList.toggle('location-header__inner--no-image', !hasImage);
    }
  }
}

if (!window.customElements.get('location-header-section')) {
  window.customElements.define('location-header-section', LocationHeaderSection);
}

class LocationStoreContentSection extends HTMLElement {
  connectedCallback() {
    this._address1El = this.querySelector('[data-lsc-address1]');
    this._address2El = this.querySelector('[data-lsc-address2]');
    this._phoneEl = this.querySelector('[data-lsc-phone]');
    this._phoneLinkEl = this.querySelector('[data-lsc-phone-link]');
    this._phoneWrapEl = this.querySelector('[data-lsc-phone-wrap]');
    this._emailEl = this.querySelector('[data-lsc-email]');
    this._emailLinkEl = this.querySelector('[data-lsc-email-link]');
    this._emailWrapEl = this.querySelector('[data-lsc-email-wrap]');
    this._directionsEl = this.querySelector('[data-lsc-directions]');
    this._hoursListEl = this.querySelector('[data-lsc-hours-list]');
    this._servicesListEl = this.querySelector('[data-lsc-services]');
    this._stores = [];

    const storesSource = this.dataset.storesSource;
    if (storesSource) {
      const sourceEl = document.getElementById(storesSource);
      if (sourceEl) {
        try {
          this._stores = JSON.parse(sourceEl.textContent || '[]');
        } catch (error) {
          this._stores = [];
        }
      }
    }

    this._fallback = {
      address1: this.dataset.fallbackAddress1 || '',
      address2: this.dataset.fallbackAddress2 || '',
      phone: this.dataset.fallbackPhone || '',
      email: this.dataset.fallbackEmail || '',
    };

    this.updateContent();
    this._syncProductCarousel();

    document.addEventListener('age-gate:store-selected', () => this.updateContent());
    window.addEventListener('storage', (event) => {
      if (event.key === '_selected_location_id') {
        this.updateContent();
      }
    });
    window.addEventListener('resize', () => this._syncProductCarousel());
  }

  _readLocationId() {
    try {
      const fromUrl = new URLSearchParams(window.location.search).get('location_id');
      if (fromUrl != null && String(fromUrl).trim() !== '') {
        return String(fromUrl).trim();
      }
    } catch (error) {
      /* ignore */
    }

    const raw = window.localStorage.getItem('_selected_location_id');
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw);
      return parsed == null ? null : String(parsed);
    } catch (error) {
      return String(raw);
    }
  }

  _setText(el, value) {
    if (!el) return;
    el.textContent = value || '';
  }

  _formatPhoneHref(phone) {
    return String(phone || '').replace(/[\s()\-]/g, '');
  }

  _updateHours(hoursRows) {
    if (!this._hoursListEl || !Array.isArray(hoursRows)) return;

    hoursRows.forEach((row) => {
      const rowEl = this._hoursListEl.querySelector(`[data-lsc-hours-row][data-day="${row.label}"]`);
      if (!rowEl) return;
      const timeEl = rowEl.querySelector('[data-lsc-hours-time]');
      this._setText(timeEl, row.text);
      rowEl.classList.toggle('is-today', Boolean(row.isToday));
    });
  }

  _updateServices(services, hasStore) {
    if (!this._servicesListEl) return;

    const types = ['pickup', 'ageVerification', 'accessible', 'parking'];
    const anyDynamic = hasStore && services && types.some((type) => services[type]);

    types.forEach((type) => {
      const placeholderItem = this._servicesListEl.querySelector(
        `[data-lsc-service][data-service-type="${type}"][data-lsc-placeholder]`
      );
      const liveItem = this._servicesListEl.querySelector(
        `[data-lsc-service][data-service-type="${type}"]:not([data-lsc-placeholder])`
      );

      if (!hasStore || !anyDynamic) {
        if (placeholderItem) placeholderItem.hidden = false;
        if (liveItem) liveItem.hidden = true;
        return;
      }

      if (placeholderItem) placeholderItem.hidden = true;
      if (liveItem) liveItem.hidden = !services[type];
    });
  }

  _syncProductCarousel() {
    const carousel = this.querySelector('[data-lsc-products-carousel]');
    if (!carousel) return;

    const track = carousel.querySelector('[data-lsc-products-track]');
    const prevBtn = carousel.querySelector('[data-lsc-products-prev]');
    const nextBtn = carousel.querySelector('[data-lsc-products-next]');
    if (!track || !prevBtn || !nextBtn) return;

    const maxScroll = track.scrollWidth - track.clientWidth;
    const scrollable = maxScroll > 4;
    carousel.classList.toggle('is-scrollable', scrollable);

    if (!scrollable) {
      prevBtn.hidden = true;
      nextBtn.hidden = true;
      return;
    }

    const atStart = track.scrollLeft <= 4;
    const atEnd = track.scrollLeft >= maxScroll - 4;
    prevBtn.hidden = atStart;
    nextBtn.hidden = atEnd;
  }

  updateContent() {
    const locationId = this._readLocationId();
    const store = this._stores.find((entry) => String(entry.id) === String(locationId));

    const address1 = store?.address1 || this._fallback.address1;
    const address2 = store?.address2 || this._fallback.address2;
    const phone = store?.phone || this._fallback.phone;
    const email = (store?.email && String(store.email).trim()) ? store.email : this._fallback.email;

    if (this._address1El) {
      this._setText(this._address1El, address1);
      this._address1El.hidden = !String(address1 || '').trim();
    }
    if (this._address2El) {
      this._setText(this._address2El, address2);
      this._address2El.hidden = !String(address2 || '').trim();
    }

    const phoneVisible = Boolean(String(phone || '').trim());
    if (this._phoneEl) this._setText(this._phoneEl, phone);
    if (this._phoneLinkEl) {
      this._phoneLinkEl.href = phoneVisible ? `tel:${this._formatPhoneHref(phone)}` : '#';
    }
    if (this._phoneWrapEl) this._phoneWrapEl.hidden = !phoneVisible;

    const emailVisible = Boolean(String(email || '').trim());
    if (this._emailEl) this._setText(this._emailEl, email);
    if (this._emailLinkEl) {
      this._emailLinkEl.href = emailVisible ? `mailto:${email}` : '#';
    }
    if (this._emailWrapEl) this._emailWrapEl.hidden = !emailVisible;

    if (this._directionsEl && store) {
      if (store.latitude != null && store.longitude != null) {
        this._directionsEl.href = `https://www.google.com/maps/dir/?api=1&destination=${store.latitude},${store.longitude}`;
      } else {
        const fullAddress = [address1, address2].filter(Boolean).join(', ');
        if (fullAddress) {
          this._directionsEl.href = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(fullAddress)}`;
        }
      }
    }

    if (store?.hoursRows) {
      this._updateHours(store.hoursRows);
    }

    this._updateServices(store?.services, Boolean(store));
  }
}

if (!window.customElements.get('location-store-content-section')) {
  window.customElements.define('location-store-content-section', LocationStoreContentSection);
}

/* Mini-cart + cart page store / fulfillment actions (survives cart-drawer AJAX refresh) */
document.addEventListener('click', (event) => {
  const nextBtn = event.target.closest('[data-mini-cart-categories-next], [data-vb-cart-popular-next], [data-lsc-products-next]');
  const prevBtn = event.target.closest('[data-mini-cart-categories-prev], [data-vb-cart-popular-prev], [data-lsc-products-prev]');
  if (nextBtn || prevBtn) {
    event.preventDefault();
    const carousel = event.target.closest('[data-mini-cart-categories-carousel], [data-vb-cart-popular-carousel], [data-lsc-products-carousel]');
    const track = carousel?.querySelector('[data-mini-cart-categories], [data-vb-cart-popular-track], [data-lsc-products-track]');
    if (!track) return;

    const card = track.querySelector('.mini-cart__category-card, .vb-cart__popular-card, .product-item, .lsc__products-card');
    const step = card ? card.getBoundingClientRect().width + 24 : track.clientWidth * 0.5;
    track.scrollBy({ left: nextBtn ? step : -step, behavior: 'smooth' });

    const lscSection = carousel?.closest('location-store-content-section');
    if (lscSection) {
      setTimeout(() => lscSection._syncProductCarousel?.(), 350);
    }
    return;
  }

  // Opening the store picker is not a cart action, so it is handled before the cart guard
  // below. It used to sit after it, and that guard returns on anything outside #mini-cart
  // or .vb-cart — so the button worked in the cart and silently did nothing on the
  // collection and search empty states, which offer "Change Store" as the way out of a
  // grid the shopper's store emptied.
  //
  // This is now the only handler for it. The header selector, the store-info section and
  // the PDP each used to bind their own copy, all doing exactly this and none of them
  // stopping propagation, so leaving them in place would fire the event twice.
  const storeBtn = event.target.closest('[data-action="open-store-picker"]');
  if (storeBtn) {
    event.preventDefault();
    window.dispatchEvent(new CustomEvent('age-gate:open-stores'));
    return;
  }

  const root = event.target.closest('#mini-cart, .vb-cart');
  if (!root) return;

  const deliveryBtn = event.target.closest('[data-action="switch-to-delivery"]');
  if (deliveryBtn) {
    event.preventDefault();
    window.dispatchEvent(new CustomEvent('product:switch-to-delivery'));
  }
});

document.addEventListener('scroll', (event) => {
  if (event.target?.matches?.('[data-lsc-products-track]')) {
    const section = event.target.closest('location-store-content-section');
    if (section) section._syncProductCarousel?.();
  }
}, true);

function syncMiniCartCategoryArrows(root = document.getElementById('mini-cart')) {
  if (!root) return;

  root.querySelectorAll('[data-mini-cart-categories-carousel]').forEach((carousel) => {
    const track = carousel.querySelector('[data-mini-cart-categories]');
    const prevBtn = carousel.querySelector('[data-mini-cart-categories-prev]');
    const nextBtn = carousel.querySelector('[data-mini-cart-categories-next]');
    if (!track || !prevBtn || !nextBtn) return;

    const maxScroll = track.scrollWidth - track.clientWidth;
    const scrollable = maxScroll > 4;
    carousel.classList.toggle('is-scrollable', scrollable);

    if (!scrollable) {
      prevBtn.hidden = true;
      nextBtn.hidden = true;
      return;
    }

    const atStart = track.scrollLeft <= 4;
    const atEnd = track.scrollLeft >= maxScroll - 4;
    prevBtn.hidden = atStart;
    nextBtn.hidden = atEnd;
  });
}

document.addEventListener('scroll', (event) => {
  if (event.target?.matches?.('[data-mini-cart-categories]')) {
    syncMiniCartCategoryArrows(event.target.closest('#mini-cart'));
  }
}, true);

window.addEventListener('resize', () => syncMiniCartCategoryArrows());

const miniCartEl = document.getElementById('mini-cart');
if (miniCartEl) {
  syncMiniCartCategoryArrows(miniCartEl);

  const observer = new MutationObserver(() => syncMiniCartCategoryArrows(miniCartEl));
  observer.observe(miniCartEl, { childList: true, subtree: true });
}

/**
 * Add the bottle deposit as its own cart line, alongside the product.
 *
 * Shoppers are shown the deposit as a separate amount, the way the old site did it. A
 * Cart Transform cannot add a line — only update, expand or merge — and `expand` yields a
 * bundle priced as a single line, which hides the amount. So the theme adds the line and
 * the Function prices it from the product's own metafield.
 *
 * This runs in the CAPTURE phase, before the theme's own ProductForm submit handler,
 * which builds its FormData from the form as it stands. Rewriting `id`/`quantity` into
 * Shopify's `items[]` shape here means both lines are added by the one request that
 * handler already makes — no second call, no window where a bottle exists without its
 * deposit.
 *
 * Anything unexpected leaves the form exactly as it was. A missing deposit is a smaller
 * failure than a broken add-to-cart button.
 */
(function depositLineOnAddToCart() {
  const INJECTED = 'data-deposit-injected';

  function intFrom(value, fallback) {
    const n = parseInt(value, 10);
    return Number.isFinite(n) && n > 0 ? n : fallback;
  }

  function hidden(form, name, value) {
    const el = document.createElement('input');
    el.type = 'hidden';
    el.name = name;
    el.value = String(value);
    el.setAttribute(INJECTED, '');
    form.appendChild(el);
  }

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;

      try {
        // Clear anything left by a previous submit of this same form.
        form.querySelectorAll(`[${INJECTED}]`).forEach((el) => el.remove());

        const marker = form.querySelector('[data-deposit-line]');
        if (!marker) return;

        const depositVariantId = marker.dataset.depositVariantId;
        if (!depositVariantId) return;

        const idInput = form.querySelector('[name="id"]');
        if (!idInput || !idInput.value) return;

        const quantityInput = form.querySelector('[name="quantity"]');
        const quantity = intFrom(quantityInput && quantityInput.value, 1);
        const perUnit = intFrom(marker.dataset.depositQuantity, 1);

        // items[] replaces id/quantity; sending both is undefined behaviour.
        const productVariantId = idInput.value;
        idInput.disabled = true;
        if (quantityInput) quantityInput.disabled = true;

        hidden(form, 'items[0][id]', productVariantId);
        hidden(form, 'items[0][quantity]', quantity);
        hidden(form, 'items[1][id]', depositVariantId);
        hidden(form, 'items[1][quantity]', quantity * perUnit);

        // Which product this deposit belongs to, and how many per unit.
        //
        // Every product shares one deposit SKU, and Shopify merges cart lines with the
        // same variant AND the same properties. Without these a 750ml and a 1.5L would
        // collapse into one deposit line of quantity 2 — a single per-unit price for two
        // different rates. Differing properties keep them apart.
        //
        // `_deposit_for` is the product's variant id, not the amount. The Function looks
        // that variant's metafield up server-side, so editing this in the page cannot
        // change what the deposit costs. The leading underscore hides both from the cart
        // and checkout display.
        hidden(form, 'items[1][properties][_deposit_for]', productVariantId);
        hidden(form, 'items[1][properties][_deposit_per_unit]', perUnit);

        // The product behind that variant, so the deposit can still find its line after a
        // store change swaps the variant out from under it. See repairOrphanedDeposits.
        if (marker.dataset.depositProduct) {
          hidden(form, 'items[1][properties][_deposit_product]', marker.dataset.depositProduct);
        }

        // The theme re-enables `id` on connect; restore both once this submit is away so
        // the form still works if the request fails and the shopper retries.
        window.setTimeout(() => {
          idInput.disabled = false;
          if (quantityInput) quantityInput.disabled = false;
          form.querySelectorAll(`[${INJECTED}]`).forEach((el) => el.remove());
        }, 0);
      } catch (error) {
        // Never let this break add-to-cart.
        form.querySelectorAll(`[${INJECTED}]`).forEach((el) => el.remove());
      }
    },
    true,
  );
})();

/**
 * Keep every deposit line matched to the product it belongs to.
 *
 * The deposit is a separate cart line so the shopper can see the amount, which means
 * nothing keeps the two in step on its own. Change a bottle from 1 to 3 and its deposit
 * stays at 1; remove the bottle and the deposit is left behind, payable, for a product
 * that is no longer in the cart. Deposits are regulated, so neither is acceptable.
 *
 * Reconciles after any cart change: each deposit line should be its product's quantity
 * times its per-unit multiplier, and gone entirely once that product is.
 *
 * Both values come from the line's own properties, written when it was added:
 *   _deposit_for       the product variant this deposit belongs to
 *   _deposit_per_unit  bottles per unit
 *   _deposit_product   the product behind that variant, which outlives a store change
 *
 * Runs to a fixed point: a correction triggers another cart update, the next pass finds
 * nothing to change, and it stops. The in-flight guard stops the two overlapping.
 */
(function keepDepositLinesInStep() {
  let running = false;

  const root = () => window.Shopify?.routes?.root || '/';

  function intFrom(value, fallback) {
    const n = parseInt(value, 10);
    return Number.isFinite(n) && n >= 0 ? n : fallback;
  }

  async function loadCart() {
    const response = await fetch(`${root()}cart.js`, {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    });
    if (!response.ok) return null;
    const cart = await response.json();
    return cart && Array.isArray(cart.items) ? cart : null;
  }

  /**
   * Tell the page a deposit line changed.
   *
   * The cart page reloads on a quantity change rather than dispatching cart:updated (see
   * theme.js), so a pass there runs after the reload against server-rendered markup.
   * Correcting the data leaves the page showing the old numbers, so it has to reload once
   * more — only ever after a real correction, so it settles.
   */
  function announce(cart) {
    if (window.themeVariables?.settings?.pageType === 'cart') {
      window.location.reload();
      return;
    }

    document.documentElement.dispatchEvent(
      new CustomEvent('cart:refresh', { bubbles: true, detail: { cart } }),
    );
  }

  /**
   * Re-point deposits whose product line was swapped for another variant.
   *
   * Changing store rewrites every line to the new store's variant
   * (utility-location-cart-inputs.liquid), and a cart update carries quantities only —
   * line properties are not something it can rewrite. So `_deposit_for` goes on naming a
   * variant that has left the cart: the pass below reads that as an orphan and removes
   * the fee for a product that is still in the cart, and the Cart Transform, which finds
   * the amount through the same property, leaves the line unpriced and unnamed until it
   * goes. One store change, three symptoms.
   *
   * `_deposit_product` survives it, because it is the same product at either store.
   *
   * Exactly one matching line, deliberately. Two lines of one product are two different
   * variants, and nothing here says which the deposit belongs to — guessing at a
   * regulated fee is worse than leaving it alone for the pass below to handle.
   */
  async function repairOrphanedDeposits(cart) {
    const productVariants = new Set();
    const linesByProduct = new Map();

    cart.items.forEach((item) => {
      if (item.properties && item.properties._deposit_for) return;
      productVariants.add(String(item.variant_id));
      const key = String(item.product_id);
      const lines = linesByProduct.get(key);
      if (lines) lines.push(item);
      else linesByProduct.set(key, [item]);
    });

    const repairs = [];
    cart.items.forEach((item) => {
      const parentId = item.properties && item.properties._deposit_for;
      if (!parentId || productVariants.has(String(parentId))) return;

      const productId = item.properties._deposit_product;
      if (!productId) return;

      const candidates = linesByProduct.get(String(productId));
      if (!candidates || candidates.length !== 1) return;

      repairs.push({ item, parent: candidates[0] });
    });

    if (repairs.length === 0) return false;

    let repaired = false;
    for (const { item, parent } of repairs) {
      // Rewriting properties gives the line a new key, which is why this sends one line
      // at a time and re-reads the cart afterwards. Other lines' keys are untouched, so
      // the keys collected above stay good for the rest of this loop.
      const response = await fetch(`${root()}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({
          id: item.key,
          quantity: item.quantity,
          properties: { ...item.properties, _deposit_for: String(parent.variant_id) },
        }),
      });
      if (response.ok) repaired = true;
    }

    return repaired;
  }

  async function reconcile() {
    if (running) return;
    running = true;

    try {
      let cart = await loadCart();
      if (!cart) return;

      // Before anything is judged an orphan, give it the chance to find its line again.
      const repaired = await repairOrphanedDeposits(cart);
      if (repaired) {
        cart = await loadCart();
        if (!cart) return;
      }

      // Quantities of the products themselves, keyed by variant id.
      const productQuantities = new Map();
      cart.items.forEach((item) => {
        if (item.properties && item.properties._deposit_for) return;
        productQuantities.set(String(item.variant_id), item.quantity);
      });

      const updates = {};
      let changed = false;

      const deposits = [];
      let resolvedAny = false;

      cart.items.forEach((item) => {
        const parentId = item.properties && item.properties._deposit_for;
        if (!parentId) return;

        const perUnit = intFrom(item.properties._deposit_per_unit, 1) || 1;
        const parentQuantity = productQuantities.get(String(parentId));
        if (parentQuantity != null) resolvedAny = true;

        deposits.push({ item, perUnit, parentQuantity });
      });

      deposits.forEach(({ item, perUnit, parentQuantity }) => {
        // An unresolved parent is only a removal when matching works in general. If NOT
        // ONE deposit line can find its product, that is the id formats disagreeing, not
        // a cart someone emptied — and treating it as removal deletes every deposit in
        // the cart a second after it was added, which is exactly what it did.
        if (parentQuantity == null && !resolvedAny) return;

        const wanted = (parentQuantity ?? 0) * perUnit;
        if (wanted !== item.quantity) {
          updates[item.key] = wanted;
          changed = true;
        }
      });

      if (!changed) {
        // A repair moves no quantity, but it does change what the line is called and what
        // the Cart Transform prices it at, so the page is stale until it re-renders.
        if (repaired) announce(cart);
        return;
      }

      const response = await fetch(`${root()}cart/update.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({ updates }),
      });
      if (!response.ok) return;

      announce(await response.json());
    } catch (error) {
      // A cart that cannot be reconciled is left as it is rather than broken further.
    } finally {
      running = false;
    }
  }

  document.documentElement.addEventListener('cart:updated', () => reconcile());

  // May already have fired: this file is loaded deferred.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => reconcile());
  } else {
    reconcile();
  }
})();

/**
 * Change a product and its deposit in ONE cart request.
 *
 * The reconciler above is a safety net, not a mechanism: it runs AFTER a cart change has
 * already landed, so removing a bottle removes the bottle now and its deposit a round
 * trip later. The shopper watches an orphaned deposit sit in the cart, and if that second
 * request never happens — the page is left, the fetch fails, the reconciler bails because
 * no deposit could be matched — it does not go at all. A regulated fee for a product that
 * is not in the cart.
 *
 * So the pairing happens up front. The theme's own control changes one line
 * (`/cart/change.js` takes a single line); this intercepts the click before
 * `line-item-quantity` sees it and sends both lines to `/cart/update.js` instead, keyed by
 * line key rather than index — indices shift the moment a line is removed.
 *
 * Capture phase, because the theme's handler is a delegated listener on the
 * `line-item-quantity` element and only stopping the event before it bubbles there will
 * do. Anything this does not recognise is left entirely alone and the theme handles it as
 * before: a product with no deposit line takes no part in any of this.
 */
(function pairDepositWithProduct() {
  const root = () => window.Shopify?.routes?.root || '/';

  function intFrom(value, fallback) {
    const n = parseInt(value, 10);
    return Number.isFinite(n) && n >= 0 ? n : fallback;
  }

  /** The deposit line belonging to a product line, if it is on the page. */
  function depositFor(productLine) {
    const variantId = productLine.dataset.variantId;
    if (!variantId) return null;
    return document.querySelector(
      `[data-cart-line][data-deposit-for="${window.CSS?.escape ? CSS.escape(variantId) : variantId}"]`,
    );
  }

  async function applyPair(productLine, deposit, quantity) {
    const productKey = productLine.dataset.lineKey;
    const depositKey = deposit.dataset.lineKey;
    if (!productKey || !depositKey) return false;

    const perUnit = intFrom(deposit.dataset.depositPerUnit, 1) || 1;

    const updates = {};
    updates[productKey] = quantity;
    updates[depositKey] = quantity * perUnit;

    const response = await fetch(`${root()}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ updates }),
    });
    if (!response.ok) return false;

    const cart = await response.json();

    if (window.themeVariables?.settings?.pageType === 'cart') {
      window.location.reload();
      return true;
    }

    document.documentElement.dispatchEvent(
      new CustomEvent('cart:updated', { bubbles: true, detail: { cart } }),
    );
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:refresh', { bubbles: true, detail: { cart } }),
    );
    return true;
  }

  /**
   * Take over an interaction, or decline it.
   *
   * Declining must be the default. Every cart line that is not a product with a deposit
   * on the page belongs to the theme, and quietly swallowing one of those would break
   * ordinary cart editing for the sake of a case that does not apply.
   */
  function handle(event, line, quantity) {
    if (!line || !Number.isFinite(quantity)) return;
    // A deposit line has no controls of its own; if one somehow fires, ignore it rather
    // than change a product from its dependent.
    if (line.dataset.depositFor) return;

    const deposit = depositFor(line);
    if (!deposit) return;

    event.preventDefault();
    event.stopPropagation();

    applyPair(line, deposit, quantity).then((ok) => {
      // A failed pair must not leave the cart half-changed or the page stale. Reloading
      // shows whatever actually landed, which is the honest thing to show.
      if (!ok) window.location.reload();
    });
  }

  document.addEventListener(
    'click',
    (event) => {
      const link = event.target.closest?.('a[href]');
      if (!link) return;
      if (link.href.indexOf('/cart/change') === -1) return;

      let quantity;
      try {
        quantity = parseInt(new URL(link.href, window.location.origin).searchParams.get('quantity'), 10);
      } catch (error) {
        return;
      }

      handle(event, link.closest('[data-cart-line]'), quantity);
    },
    true,
  );

  document.addEventListener(
    'change',
    (event) => {
      const input = event.target;
      if (!input || input.tagName !== 'INPUT' || input.name !== 'updates[]') return;

      handle(event, input.closest?.('[data-cart-line]'), parseInt(input.value, 10));
    },
    true,
  );
})();

/**
 * `?debug_deposits=1` — why a deposit line is or is not where you expect.
 *
 * The failure modes are silent and look identical from the cart: the line was never
 * added, the line was added and then reconciled away, or it is there at the wrong price
 * because the Function did not recognise it.
 */
(function depositDebug() {
  let on = false;
  try {
    on =
      window.location.search.indexOf('debug_deposits') !== -1 ||
      window.localStorage.getItem('vb_debug_deposits') === '1';
  } catch (error) {
    return;
  }
  if (!on) return;

  async function dump(label) {
    try {
      const cart = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      }).then((r) => r.json());

      const rows = cart.items.map((i) => ({
        title: i.title,
        variant_id: i.variant_id,
        qty: i.quantity,
        price: i.price / 100,
        deposit_for: (i.properties && i.properties._deposit_for) || '',
        per_unit: (i.properties && i.properties._deposit_per_unit) || '',
      }));
      console.log(`%c[deposits] ${label}`, 'color:#0a7;font-weight:700');
      console.table(rows);

      const products = new Set(
        cart.items.filter((i) => !(i.properties && i.properties._deposit_for))
          .map((i) => String(i.variant_id)),
      );
      cart.items.forEach((i) => {
        const p = i.properties && i.properties._deposit_for;
        if (!p) return;
        if (!products.has(String(p))) {
          console.warn(
            `[deposits] line "${i.title}" points at ${p}, which is not a product line in ` +
              `this cart. Product variant ids present: ${[...products].join(', ') || '(none)'}`,
          );
        }
      });
    } catch (error) {
      console.warn('[deposits] could not read the cart', error);
    }
  }

  // Why a form is or is not going to add a deposit, before anything is clicked.
  function reportForms() {
    const markers = document.querySelectorAll('[data-deposit-variant-id]');
    if (markers.length === 0) {
      console.warn(
        '[deposits] no product form on this page renders deposit-cart-inputs. Either this ' +
          'is not a product page, or that form has not been wired up yet.',
      );
      return;
    }
    console.log('%c[deposits] product forms on this page', 'color:#0a7;font-weight:700');
    console.table(
      Array.from(markers).map((el) => ({
        will_add_deposit: el.hasAttribute('data-deposit-line'),
        deposit_variant_id: el.dataset.depositVariantId || '(theme setting empty)',
        product_has_bottle_deposit: el.dataset.depositHasAmount,
        resolved_from: el.dataset.depositSource || '(nothing resolved)',
        per_unit: el.dataset.depositQuantity,
      })),
    );
    Array.from(markers).forEach((el) => {
      if (el.hasAttribute('data-deposit-line')) return;
      if (el.dataset.depositHasTarget !== 'true') {
        console.warn('[deposits] the theme setting "Bottle deposit variant id" is empty.');
      } else if (el.dataset.depositHasAmount !== 'true') {
        console.warn(
          '[deposits] this variant has no product.bottle_deposit metafield, so no deposit ' +
            'applies to it. That is correct for products that do not carry one.',
        );
      }
    });
  }

  document.documentElement.addEventListener('cart:updated', () => dump('after cart:updated'));
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => dump('on load'));
  } else {
    dump('on load');
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', reportForms);
  } else {
    reportForms();
  }
})();

/* ---------------------------------------------------------------------------
   Collection / search — sort by displayed store price (short-term)

   Shopify's price-ascending / price-descending uses Admin variant price. Cards show
   location metafield pricing. Re-order the loaded grid client-side by
   data-display-price-cents on each product-item (same cents as the card).
   --------------------------------------------------------------------------- */
function getCatalogDisplayPriceSortDirection() {
  const sortBy = new URLSearchParams(window.location.search).get('sort_by');
  if (sortBy === 'price-ascending') return 'asc';
  if (sortBy === 'price-descending') return 'desc';
  return null;
}

function sortCatalogGridsByDisplayPrice(root = document) {
  const direction = getCatalogDisplayPriceSortDirection();
  if (!direction) return;

  root.querySelectorAll('.product-list--catalog .product-list__inner').forEach((grid) => {
    const items = Array.from(grid.children).filter(
      (node) => node.matches && node.matches('product-item')
    );
    if (items.length < 2) return;

    const ordered = items.map((element, index) => ({
      element,
      index,
      cents: Number.parseInt(element.getAttribute('data-display-price-cents') || '', 10) || 0,
    }));

    ordered.sort((a, b) => {
      if (a.cents !== b.cents) {
        return direction === 'asc' ? a.cents - b.cents : b.cents - a.cents;
      }
      return a.index - b.index;
    });

    /* Already in order: leave the DOM alone. Re-appending fires the MutationObserver
       below, which sorted again, forever — every card was detached and re-attached
       every 40ms, so a click never landed on the element it started on. */
    if (ordered.every(({ index }, position) => index === position)) return;

    const fragment = document.createDocumentFragment();
    ordered.forEach(({ element }) => fragment.appendChild(element));
    grid.appendChild(fragment);
  });
}

function initCatalogDisplayPriceSort() {
  const facetMain = document.querySelector('#facet-main');
  if (!facetMain) return;

  const apply = () => {
    if (!getCatalogDisplayPriceSortDirection()) return;
    sortCatalogGridsByDisplayPrice(facetMain);
  };

  apply();

  let debounceTimer;
  const observer = new MutationObserver(() => {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(apply, 40);
  });
  observer.observe(facetMain, { childList: true, subtree: true });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCatalogDisplayPriceSort);
} else {
  initCatalogDisplayPriceSort();
}

/* ---------------------------------------------------------------------------
   Collection / search infinite scroll

   The theme removes product cards after Shopify has already paginated — not sold in
   this province, not stocked at this store — so a 50-per-page collection or search
   can render 30 cards on page 1 and none on page 3. Numbered pages describe Shopify's
   result set rather than the one on screen, which is how a shopper ends up clicking
   into an empty page 4 that says it has products.

   Scrolling has no page numbers to be wrong about, and a page that renders nothing is
   something to step over rather than something to show. So this fetches ahead, keeps a
   buffer of pages that actually produced cards, and skips the ones that did not.

   Used by both main-collection and main-search (product results). Lives inside
   #facet-main, which ProductFacet replaces wholesale when filters change, so a filter
   change disconnects this element and reconnects a fresh one — no reset logic of its
   own, and no stale buffer from the previous filter.
   --------------------------------------------------------------------------- */
class CollectionInfiniteScroll extends HTMLElement {
  connectedCallback() {
    const root = this.closest('#facet-main') || document;
    this.grid = root.querySelector('.product-list__inner');
    this.sentinel = this.querySelector('[data-infinite-sentinel]');
    this.statusEl = this.querySelector('[data-infinite-status]');
    this.nav = this.querySelector('page-pagination');

    this.sectionId = this.dataset.sectionId || '';
    this.nextUrl = (this.dataset.nextPage || '').trim();
    this.bufferCards = Math.max(1, Number(this.dataset.bufferCards) || 12);
    this.maxFetchesPerFill = Math.max(1, Number(this.dataset.maxFetches) || 6);
    this.queue = [];
    this.loading = false;
    this.pendingAdvance = false;
    this.done = !this.nextUrl;

    if (!this.grid || !this.sentinel || this.done) {
      this._finish();
      return;
    }

    // Page 1 can render nothing at all (every product on it filtered out for this store)
    // while later pages still have products. The server then puts the "Nothing here for
    // your store" empty state in the grid, and the cards appended below it would sit next
    // to a message saying there are none. Hold it back while there are pages left to load;
    // it is removed when a card arrives, or shown again if the scroll ends empty.
    this.emptyState = this.grid.querySelector(':scope > .empty-state');
    if (this.emptyState && !this.grid.querySelector(':scope > product-item')) {
      // Inline display rather than `hidden`: .empty-state sets its own display, which
      // would override the attribute.
      this.emptyState.style.display = 'none';
    } else {
      this.emptyState = null;
    }

    // Paging is this element's job now. The nav stays in the markup so the collection
    // still works without JavaScript; it is only hidden once we know we are running.
    if (this.nav) this.nav.hidden = true;

    // Ahead of the fold: the next page should be in the buffer before the shopper
    // reaches the bottom, so the grid grows without a visible gap.
    this.observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) this._advance();
      },
      { rootMargin: '800px 0px' }
    );
    this.observer.observe(this.sentinel);

    this._fill();
  }

  disconnectedCallback() {
    this.aborted = true;
    if (this.observer) this.observer.disconnect();
  }

  _bufferedCards() {
    return this.queue.reduce((total, cards) => total + cards.length, 0);
  }

  _setStatus(text) {
    if (this.statusEl) this.statusEl.textContent = text || '';
  }

  // A card arrives carrying `reveal`, which is `opacity: 0` until something animates it
  // in. That something is ProductList, and it snapshots its items in the CONSTRUCTOR —
  // so a card appended afterwards is not in the list it animates and stays invisible for
  // good. The page would grow and grow while showing nothing.
  //
  // Stripping the attribute is the fix rather than re-running the animation: these cards
  // arrive while the shopper is already reading the grid, where a staggered fade is
  // decoration for an entrance they did not see.
  _reveal(card) {
    card.removeAttribute('reveal');
    card.removeAttribute('reveal-visibility');
    if (typeof card.querySelectorAll !== 'function') return;
    card.querySelectorAll('[reveal], [reveal-visibility]').forEach((node) => {
      node.removeAttribute('reveal');
      node.removeAttribute('reveal-visibility');
    });
  }

  async _fetchNextPage() {
    if (!this.nextUrl) return null;

    let url;
    try {
      url = new URL(this.nextUrl, window.location.origin);
    } catch (error) {
      return null;
    }
    // Section rendering returns just this section, so the response is the grid rather
    // than a whole document with a second header and footer in it.
    url.searchParams.set('section_id', this.sectionId);

    const response = await fetch(url.toString());
    if (!response.ok) return null;

    const holder = document.createElement('div');
    holder.innerHTML = await response.text();

    const nextGrid = holder.querySelector('.product-list__inner');
    // Only real product cards. An empty page renders an .empty-state inside the grid;
    // counting that as content would append dead ends into a grid that already has
    // products, and would stop the fill loop from stepping over the empty page.
    const cards = nextGrid
      ? Array.from(nextGrid.children).filter((el) => el.matches && el.matches('product-item'))
      : [];

    // The fetched page carries its own nav, so the next URL comes from the response
    // rather than being assembled here. A page with no rel="next" is the last one.
    const nextLink = holder.querySelector('page-pagination a[rel="next"]');
    this.nextUrl = nextLink ? nextLink.getAttribute('href') : '';

    return cards;
  }

  async _fill() {
    if (this.loading || this.done || this.aborted) return;
    this.loading = true;
    this._setStatus(this.dataset.loadingLabel);

    try {
      // Buffered by CARD count, not page count. With a store filter applied the theme can
      // remove most of a 50-product page — measured at two survivors per page on a real
      // collection — so "two pages ahead" was about four products, and the shopper hit the
      // bottom again almost immediately. Pages that produced nothing are stepped over
      // inside this loop and never reach them.
      // Capped per fill. On a collection where the theme removes nearly everything, the
      // card target alone could mean a dozen sequential section renders in one burst.
      // Stopping short leaves the rest for the next scroll, which is a shorter wait for
      // the shopper and a gentler pattern against the shop.
      let fetches = 0;
      while (
        this._bufferedCards() < this.bufferCards &&
        this.nextUrl &&
        !this.aborted &&
        fetches < this.maxFetchesPerFill
      ) {
        fetches += 1;
        const cards = await this._fetchNextPage();
        if (cards === null) {
          this.nextUrl = '';
          break;
        }
        if (cards.length > 0) this.queue.push(cards);
      }
      if (!this.nextUrl) this.done = true;
    } catch (error) {
      // A failed fetch stops the scroll rather than retrying in a loop. The nav comes
      // back so the shopper still has a way through the rest of the collection.
      this.done = true;
      if (this.nav) this.nav.hidden = false;
    } finally {
      this.loading = false;
      this._setStatus('');
    }

    if (this.pendingAdvance && this.queue.length > 0) {
      this.pendingAdvance = false;
      this._advance();
      return;
    }

    // The cap was reached without a single card: every page fetched had been emptied by
    // the filters. Nothing appended, so the sentinel has not moved and the observer will
    // not fire again — without this the scroll stops silently part way through a
    // collection that still has products further down. Continues on the next tick so the
    // run stays in bounded bursts rather than one long block.
    if (this.pendingAdvance && !this.done && !this.aborted) {
      setTimeout(() => this._fill(), 0);
      return;
    }

    if (this.done && this.queue.length === 0) this._finish();
  }

  _advance() {
    if (this.aborted) return;

    if (this.queue.length === 0) {
      // Asked for more before the buffer was ready. Remember it, because the sentinel
      // may already be off screen by the time the fetch lands and would not fire again.
      if (!this.done) this.pendingAdvance = true;
      this._fill();
      return;
    }

    // Drains the whole buffer rather than one page. Every page in it is content the
    // shopper is about to scroll into, and releasing it two products at a time is what
    // made the grid crawl.
    const fragment = document.createDocumentFragment();
    while (this.queue.length > 0) {
      this.queue.shift().forEach((card) => {
        this._reveal(card);
        fragment.appendChild(card);
      });
    }
    if (this.emptyState && fragment.childNodes.length > 0) {
      this.emptyState.remove();
      this.emptyState = null;
    }
    this.grid.appendChild(fragment);

    this._fill();

    if (this.done && this.queue.length === 0) this._finish();
  }

  _finish() {
    if (this.observer) this.observer.disconnect();
    if (this.sentinel) this.sentinel.hidden = true;

    // Every remaining page came back empty too, so the empty state was right after all.
    if (this.emptyState) {
      this.emptyState.style.display = '';
      this._setStatus('');
      return;
    }

    this._setStatus(this.dataset.endLabel);
  }
}

if (!window.customElements.get('collection-infinite-scroll')) {
  window.customElements.define('collection-infinite-scroll', CollectionInfiniteScroll);
}

/* ---------------------------------------------------------------------------
   "Shop this store" on store pages

   Picks the page's store the same way the header store picker does — the age gate's
   confirm step, the cart warning, the cart-update form — and then lands on the link's
   page instead of reloading this one. With the store already selected, or no age gate
   on the page, the link is just a link.
   --------------------------------------------------------------------------- */
document.addEventListener('click', (event) => {
  const link = event.target.closest && event.target.closest('a[data-select-store-id]');
  if (!link) return;
  if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

  const storeId = (link.dataset.selectStoreId || '').trim();
  if (!storeId || link.dataset.storeSelected === 'true') return;
  if (!document.querySelector('[id^="AgeGate-"][x-data]')) return;

  event.preventDefault();

  const url = new URL(link.href, window.location.href);
  const returnTo = url.origin === window.location.origin ? url.pathname + url.search + url.hash : null;

  window.dispatchEvent(
    new CustomEvent('age-gate:request-select-store', {
      detail: { storeId, returnTo },
    })
  );
});
